'use client';

import type { Cta } from '@/libs/contentful/types';
import type { LinkedIconProps } from '@/libs/contentful/types';
import { CtaAddOnProps, CtaAddOnVariant, CtaAddOnAlignment } from './types';
import KeyLine from '@/components/shared/KeyLine';
import RichTextHeadline from '@/components/shared/RichTextHeadline/RichTextHeadline';
import { KeyLinePosition, Margins } from '@/types/constants';
import { cn, getColorwayWithTransparency, getSectionProps } from '@/utils/styles';
import { liveUpdateAttrs } from '@/libs/contentful/utils';
import CTA from 'components/shared/CTA/CTA';
import LinkedIcon from 'components/shared/LinkedIcon/LinkedIcon';
import { useEffect, useState } from 'react';

export enum CtaTypes {
  Cta = 'Cta',
  LinkedIcon = 'LinkedIcon',
}

const getButtonOnlyYMargins = (margins: Margins) => {
  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: 'pt-0 pb-6',
    [Margins.NoBottom]: 'pt-6 pb-0',
    [Margins.NoMargins]: 'pt-1 pb-2', // Add tiny top and bottom margins to prevent button from being completely flush with background color
    [Margins.Centered]: 'py-6',
  };

  return marginStyles[margins] || marginStyles[Margins.NoMargins];
};

const getKeylinePadding = (margins: Margins, showTopKeyline: boolean): string => {
  if (margins == Margins.Centered) {
    if (showTopKeyline) {
      return 'pt-12';
    } else {
      return 'sm:pt-20 pt-12';
    }
  }
  return '';
};

const CtaRenderer = ({ cta, className }: { cta: Cta | LinkedIconProps; className?: string }) => {
  switch (cta.__typename) {
    case CtaTypes.Cta:
      return <CTA cta={cta as Cta} className={className} />;
    case CtaTypes.LinkedIcon:
      return <LinkedIcon {...(cta as LinkedIconProps)} />;
    default:
      return null;
  }
};

const CtaAddOn = ({
  sys,
  sectionName,
  color,
  keyLine,
  margins,
  headline,
  cta,
  variant,
  alignment,
  transparentBackground,
  pinCtaAboveFold,
}: CtaAddOnProps) => {
  const showTopKeyline = keyLine === KeyLinePosition.Top;
  const [bannerHeight, setBannerHeight] = useState(0);

  // For CTAs pinned above the fold, we need the bottom position to
  // be adjusted based on the height of the disclosure banner (if present)
  useEffect(() => {
    if (!pinCtaAboveFold) return;

    const updateBannerHeight = () => {
      const banner = document.querySelector('[data-disclosure-banner]');
      setBannerHeight(banner?.clientHeight || 0);
    };

    updateBannerHeight();

    // Listen for banner dismissal event from DisclosureBanner
    const handleBannerDismissed = () => setBannerHeight(0);
    window.addEventListener('disclosureBannerDismissed', handleBannerDismissed);

    return () => window.removeEventListener('disclosureBannerDismissed', handleBannerDismissed);
  }, [pinCtaAboveFold]);

  const stickyBottomStyle = pinCtaAboveFold && bannerHeight > 0 ? { bottom: `${bannerHeight}px` } : undefined;

  if (variant === CtaAddOnVariant.ButtonOnly)
    return (
      <section
        {...getSectionProps(
          undefined,
          undefined,
          sectionName,
          getColorwayWithTransparency(color, transparentBackground),
        )}
        data-testid="copyAddOnWithButton"
        className={cn(
          pinCtaAboveFold &&
            'max-sm:sticky max-sm:bottom-0 max-sm:px-4 max-sm:z-24 max-sm:transition-[bottom] max-sm:duration-300',
        )}
        style={stickyBottomStyle}>
        <div className={cn('content-wrapper', getButtonOnlyYMargins(margins))}>
          <div
            className={cn(' flex min-w-full', {
              'justify-center': alignment === CtaAddOnAlignment.Centered,
              'justify-end': alignment === CtaAddOnAlignment.Right,
            })}>
            <CtaRenderer cta={cta} className={'sm:max-w-fit'} />
          </div>
        </div>
      </section>
    );

  return (
    <section {...getSectionProps(color, undefined, sectionName)} data-testid="copyAddOnWithButton">
      <div className="content-wrapper">
        {showTopKeyline && <KeyLine />}
        <div className={cn('sm:pb-20 pb-12', getKeylinePadding(margins, showTopKeyline))}>
          <div className="flex flex-col sm:flex-row justify-between gap-6">
            {headline && (
              <div
                className="basis-1/2 text-heading-lg-mobile md:text-heading-lg font-sans font-semibold"
                {...liveUpdateAttrs(sys?.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}

            <div className="basis-1/2 flex flex-col sm:items-end">
              <CtaRenderer cta={cta} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaAddOn;
